const MAGIC_AUTH_STORAGE_KEY = 'client-magic-auth-request-v1';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface StoredMagicAuthRequest {
  email: string;
  codeId: string;
  expiresAt: string;
  retryAt: string;
}

export type MagicLinkEntry = { mode: 'manual' } | { mode: 'link'; codeId: string; code: string; valid: boolean };

export interface MagicAuthDomainError {
  code?: string;
  details?: {
    attemptsLeft?: number;
    retryAt?: string;
    retryAfterSeconds?: number;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isValidTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

export const isValidMagicEmail = (email: string): boolean => {
  const normalized = email.trim();
  return normalized.length <= 320 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
};

export const isValidMagicCode = (code: string): boolean => /^\d{6}$/.test(code);

export const saveMagicAuthRequest = (request: StoredMagicAuthRequest): void => {
  if (typeof window === 'undefined') return;

  window.sessionStorage.setItem(
    MAGIC_AUTH_STORAGE_KEY,
    JSON.stringify({
      email: request.email,
      codeId: request.codeId,
      expiresAt: request.expiresAt,
      retryAt: request.retryAt,
    }),
  );
};

export const clearMagicAuthRequest = (): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(MAGIC_AUTH_STORAGE_KEY);
};

export const readMagicAuthRequest = (): StoredMagicAuthRequest | null => {
  if (typeof window === 'undefined') return null;

  const stored = window.sessionStorage.getItem(MAGIC_AUTH_STORAGE_KEY);
  if (!stored) return null;

  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      !isRecord(parsed) ||
      typeof parsed.email !== 'string' ||
      typeof parsed.codeId !== 'string' ||
      !isValidTimestamp(parsed.expiresAt) ||
      !isValidTimestamp(parsed.retryAt) ||
      !isValidMagicEmail(parsed.email) ||
      !UUID_PATTERN.test(parsed.codeId)
    ) {
      clearMagicAuthRequest();
      return null;
    }

    return {
      email: parsed.email,
      codeId: parsed.codeId,
      expiresAt: parsed.expiresAt,
      retryAt: parsed.retryAt,
    };
  } catch {
    clearMagicAuthRequest();
    return null;
  }
};

export const readAndClearMagicLinkEntry = (): MagicLinkEntry => {
  if (typeof window === 'undefined') return { mode: 'manual' };

  const params = new URLSearchParams(window.location.search);
  const codeId = params.get('codeId');
  const code = params.get('code');
  if (codeId === null && code === null) return { mode: 'manual' };

  window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`);

  return {
    mode: 'link',
    codeId: codeId ?? '',
    code: code ?? '',
    valid: UUID_PATTERN.test(codeId ?? '') && isValidMagicCode(code ?? ''),
  };
};

export const getMagicAuthDomainError = (error: unknown): MagicAuthDomainError => {
  if (!isRecord(error) || !isRecord(error.data)) return {};

  const { code, details } = error.data;
  if (typeof code !== 'string') return {};

  if (!isRecord(details)) return { code };

  const domainDetails: MagicAuthDomainError['details'] = {};
  if (typeof details.attemptsLeft === 'number') domainDetails.attemptsLeft = details.attemptsLeft;
  if (typeof details.retryAt === 'string') domainDetails.retryAt = details.retryAt;
  if (typeof details.retryAfterSeconds === 'number') domainDetails.retryAfterSeconds = details.retryAfterSeconds;

  return { code, details: domainDetails };
};
